import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { parentApi, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Button, Modal, Pill } from "../../components/ui";
import Paywall from "../../components/Paywall";
import { supported as notifSupported, isNotifyOn, enableNotifications, disableNotifications } from "../../lib/notify";
import Icon from "../../components/Icon";

export default function Settings() {
  const { user, family, logout, isDemo, isOwner, isPaused, isNativeBilling, manageSubscription, syncStripe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notif, setNotif] = useState(isNotifyOn());
  const [showPaywall, setShowPaywall] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [checkoutState, setCheckoutState] = useState("");
  const checkoutReturn = new URLSearchParams(location.search).get("checkout");

  const status = family?.subscription_status;
  const active = !isPaused && (status === "active" || status === "canceled");
  const ending = status === "canceled" || family?.subscription_cancel_at_period_end;
  const stripeCustomer = family?.has_stripe_customer || family?.subscription_provider === "stripe";
  const complimentary = family?.access_reason === "complimentary";

  useEffect(() => {
    if (!isOwner || isNativeBilling || !checkoutReturn) return;
    if (checkoutReturn === "canceled") { setCheckoutState("canceled"); return; }
    if (checkoutReturn !== "success") return;
    let canceled = false;
    let timer;
    setCheckoutState("checking");
    const check = async (attempt = 0) => {
      try {
        const updated = await syncStripe();
        if (canceled) return;
        if (updated?.has_access && updated?.access_reason === "subscription") { setCheckoutState("confirmed"); return; }
      } catch (e) { if (canceled) return; }
      if (attempt < 5) timer = setTimeout(() => check(attempt + 1), 2000);
      else setCheckoutState("pending");
    };
    check();
    return () => { canceled = true; clearTimeout(timer); };
  }, [checkoutReturn, isOwner, isNativeBilling, syncStripe]);

  const openPortal = async () => {
    if (isNativeBilling) { toast.message("This plan was purchased on the website. Visit ChoreMouse in your browser to manage billing."); return; }
    setBillingBusy(true);
    try { await manageSubscription(); } catch (e) { toast.error(apiError(e)); }
    finally { setBillingBusy(false); }
  };

  const toggleNotif = async () => {
    if (notif) { disableNotifications(); setNotif(false); toast.success("Reminders turned off"); return; }
    const ok = await enableNotifications();
    if (ok) { setNotif(true); toast.success("Reminders on — we'll nudge you about approvals 🔔"); }
    else { toast.error("Your browser blocked notifications. Allow them in your browser settings."); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await parentApi.delete("/account");
      toast.success("Your account and data were deleted.");
      await logout();
      navigate("/");
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div><h1 className="font-heading font-black text-3xl text-ink">Settings</h1><p className="text-ink-soft font-medium">Your account and preferences.</p></div>

      {checkoutState && <div className="rounded-3xl bg-primary/10 p-5 text-sm font-semibold text-ink" role="status" data-testid="checkout-status">
        {checkoutState === "checking" ? "Confirming your payment securely…" : checkoutState === "confirmed" ? "Payment confirmed. Your family adventure is ready!" : checkoutState === "canceled" ? "Checkout was canceled. You can choose a plan whenever you're ready." : "Your payment is still being confirmed. Access will resume after your subscription is verified."}
        {checkoutState === "pending" && <button onClick={async () => { setBillingBusy(true); try { const updated = await syncStripe(); if (updated?.has_access && updated?.access_reason === "subscription") setCheckoutState("confirmed"); else toast.message("Still waiting for payment confirmation."); } catch (e) { toast.error(apiError(e)); } finally { setBillingBusy(false); } }} disabled={billingBusy} className="block underline font-bold mt-2" data-testid="checkout-check-again">{billingBusy ? "Checking…" : "Check again"}</button>}
      </div>}

      <Card className="p-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 grid place-items-center text-primary"><Icon name="user" width={24} height={24} /></div>
          <div className="min-w-0"><div className="font-heading font-extrabold text-ink truncate">{user?.name}</div><div className="text-sm text-ink-soft font-medium truncate">{user?.email}</div></div>
        </div>
      </Card>

      <Card kid className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Icon name="sparkles" className="text-primary" width={22} height={22} /><span className="font-heading font-extrabold text-lg text-ink">ChoreMouse Plus</span></div>
          {isDemo ? <Pill tone="sunny">Demo</Pill> : isPaused ? <Pill tone="coral">Paused</Pill> : active ? <Pill tone="primary">{ending ? "Ends after this period" : "Active"}</Pill> : status === "trial" ? <Pill tone="sunny">{family?.trial_days_left} days left</Pill> : <Pill tone="slate">Inactive</Pill>}
        </div>
        {isDemo ? (
          <>
            <p className="text-sm text-ink-soft font-medium my-3">You're exploring the demo — it's read-only. Start your own family to save chores, kids and rewards.</p>
            <Button className="w-full" onClick={() => navigate("/register")} data-testid="start-my-family"><Icon name="rocket" width={18} height={18} /> Start my family</Button>
          </>
        ) : !isOwner ? (
          <p className="text-sm text-ink-soft font-medium my-3">{isPaused ? "Your family's adventure is paused. Ask the family owner to enroll; your progress is saved." : "Your plan is managed by the family owner."}</p>
        ) : (
          <>
            <p className="text-sm text-ink-soft font-medium my-3">
              {isPaused ? "Your family's access is paused. Enroll to continue; your kids, missions and rewards are saved."
                : complimentary ? "Your family has complimentary access. No subscription payment is needed."
                : active ? `Your ${family?.plan === "annual" ? "annual" : "monthly"} plan includes your whole family.${ending ? " Renewal is off; access continues until the paid period ends." : ""}`
                : "Your seven-day trial includes full access. Choose a plan to keep the adventure going after it ends."}
            </p>
            {complimentary ? null : stripeCustomer ? <Button variant="outline" className="w-full" disabled={billingBusy} onClick={openPortal} data-testid="manage-billing"><Icon name="credit-card" width={18} height={18} /> {billingBusy ? "Opening…" : "Manage subscription"}</Button>
              : active ? <div className="text-sm font-semibold text-ink-soft" data-testid="native-manage-billing">Manage or cancel with the store you used:<div className="flex flex-wrap gap-4 mt-2"><a className="underline text-primary-dark" data-testid="manage-apple-subscription" href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noreferrer">App Store</a><a className="underline text-primary-dark" data-testid="manage-google-subscription" href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noreferrer">Google Play</a></div></div> : null}
            {!active && <Button className="w-full mt-3" onClick={() => setShowPaywall(true)} data-testid="open-paywall"><Icon name="crown" width={18} height={18} /> See plans & enroll</Button>}
          </>
        )}
      </Card>

      <Card kid className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Icon name="bell" className="text-primary" width={22} height={22} /><span className="font-heading font-extrabold text-lg text-ink">Reminders</span></div>
          {notif ? <Pill tone="primary">On</Pill> : <Pill tone="slate">Off</Pill>}
        </div>
        <p className="text-sm text-ink-soft font-medium my-3">Get a browser notification the moment kids finish a chore or ask for a reward.</p>
        {notifSupported() ? (
          <Button variant={notif ? "outline" : "primary"} className="w-full" onClick={toggleNotif} data-testid="toggle-notifications">
            <Icon name={notif ? "bell-off" : "bell"} width={18} height={18} /> {notif ? "Turn off reminders" : "Turn on reminders"}
          </Button>
        ) : (
          <p className="text-xs font-bold text-ink-muted">Notifications aren't supported in this browser.</p>
        )}
        <p className="text-[11px] text-ink-muted font-semibold mt-2 text-center">Works while ChoreMouse is open. Full background push arrives with the mobile app.</p>
      </Card>

      {family?.access_code && !isPaused && (
      <Card kid className="p-5" data-testid="family-settings-card">
        <div className="flex items-center gap-2"><Icon name="users" className="text-primary" width={22} height={22} /><span className="font-heading font-extrabold text-lg text-ink">Family & kids</span></div>
        <p className="text-sm text-ink-soft font-medium my-3">Add or edit kids, set PINs, weekly goals and avatars.</p>
        <Button className="w-full" onClick={() => navigate("/app/family")} data-testid="open-family"><Icon name="users" width={18} height={18} /> Manage family</Button>
      </Card>
      )}

      <Card className="p-2">
        {[
          { to: "/parents", icon: "shield-check", label: "For Parents (safety & COPPA)" },
          { to: "/privacy", icon: "lock", label: "Privacy Policy" },
          { to: "/terms", icon: "file-text", label: "Terms of Service" },
          { to: "/support", icon: "life-buoy", label: "Support & contact" },
        ].map((l) => (
          <Link key={l.to} to={l.to} className="flex items-center gap-3 px-3 py-3.5 rounded-2xl hover:bg-slate-50 transition-colors" data-testid={`link-${l.icon}`}>
            <Icon name={l.icon} className="text-ink-soft" width={20} height={20} /><span className="font-bold text-ink flex-1">{l.label}</span><Icon name="chevron-right" className="text-ink-muted" width={18} height={18} />
          </Link>
        ))}
      </Card>

      <Button variant="outline" className="w-full" onClick={logout} data-testid="settings-logout"><Icon name="log-out" width={18} height={18} /> Log out</Button>

      {isOwner && !isDemo && (
      <Card className="p-5 border-coral/30">
        <div className="flex items-center gap-2 mb-1"><Icon name="triangle-alert" className="text-coral" width={20} height={20} /><span className="font-heading font-extrabold text-ink">Delete account</span></div>
        <p className="text-sm text-ink-soft font-medium mb-4">Permanently delete your family, kids, chores and rewards. This cannot be undone.</p>
        <p className="text-sm text-ink-soft font-medium mb-4">Website subscriptions are canceled during deletion. If you subscribed through Apple or Google, cancel in your store account too.</p>
        <Button variant="coral" className="w-full" onClick={() => setConfirm(true)} data-testid="delete-account-btn">Delete my account</Button>
      </Card>
      )}

      <Modal open={confirm} onClose={() => setConfirm(false)} title="Delete account?" testid="delete-modal">
        <p className="text-ink-soft font-medium mb-5">This will permanently remove <b className="text-ink">everything</b> for your family. There's no way to get it back.</p>
        <p className="text-sm text-ink-soft font-medium mb-5">Your website subscription will be canceled. App Store or Google Play subscriptions must be canceled separately in your store account.</p>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => setConfirm(false)} data-testid="cancel-delete">Cancel</Button>
          <Button variant="coral" className="flex-1" onClick={deleteAccount} disabled={busy} data-testid="confirm-delete">{busy ? "Deleting…" : "Yes, delete"}</Button>
        </div>
      </Modal>

      <Paywall open={showPaywall} onClose={() => setShowPaywall(false)} />
    </div>
  );
}
